import usePrivateAxios from "@/common/api/usePrivateAxios";
import { transformNewDriverLicenseCategories } from "@/common/utils/driverLicenseCategoryTransformers";
import { getRailsUpdate } from "@/common/utils/getRailsUpdate";
import { mapToDeletion } from "@/common/utils/railsHelpers";
import { transformNewRoles } from "@/common/utils/roleTransformers";
import { removeSpecialCharacters } from "@/common/utils/unFormatters";
import { dateBrStringToISO } from "@/common/utils/dateTransformer";
import { CompanyUserValidationSchema } from "@/features/privateCompanies/schemas/create.schema";
import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { CompanyUserData } from "../types/companyUsers.type";

interface UseUpdateCompanyUser {
  companyUser: CompanyUserData | undefined;
  contactId?: string,
  companyId?: string,
  file: Blob | undefined
  onSuccess: () => void,
  onError: (error: AxiosError) => void
}

const useUpdateCompanyUser = ({
  companyUser,
  contactId,
  companyId,
  file,
  onSuccess,
  onError,
}: UseUpdateCompanyUser) => {
  const privateAxios = usePrivateAxios();
  return useMutation({
    mutationKey: ['updateCompanyUser'],
    mutationFn: async ({ form }: {
      form: CompanyUserValidationSchema;
    }) => {
      const normalizedDriverLicenseNumber =
        form.current_driver_license_registration_number
          ? removeSpecialCharacters(
              form.current_driver_license_registration_number,
            )
          : null;

      const normalizedDriverLicenseExpirationDate =
        form.current_driver_license_expiration_date
          ? dateBrStringToISO(
              form.current_driver_license_expiration_date,
            ) || null
          : null;

      const normalizedDateOfBirth = dateBrStringToISO(form.date_of_birth);

      const normalizedEnrollmentNumber = form.enrollment_number?.trim()
        ? form.enrollment_number.trim()
        : null;

      const normalizedNameOnCard = form.name_on_card?.trim()
        ? form.name_on_card.trim()
        : null;

      const payload = {
        company_id: companyId,
        cpf: form.cpf ? removeSpecialCharacters(form.cpf) : undefined,
        date_of_birth: normalizedDateOfBirth,
        full_name: form.full_name,
        status: form?.status?.value || 'enabled',
        branch_id: form?.branch_id?.value || null,
        phone_attributes: {
          id: companyUser?.phone?.id,
          number: form?.phone?.number
            ? removeSpecialCharacters(form?.phone?.number)
            : '',
        },
        email: form.email || '',
        enrollment_number: normalizedEnrollmentNumber,
        user_roles_attributes: getRailsUpdate(
          companyUser?.user_roles || [],
          form?.roles || [],
          (userRole) => userRole.role.id,
          (role) => role.value || '',
          mapToDeletion,
          transformNewRoles,
        ),
        current_driver_license_registration_number: normalizedDriverLicenseNumber,
        current_driver_license_expiration_date: normalizedDriverLicenseExpirationDate,
        user_driver_license_categories_attributes: getRailsUpdate(
          companyUser?.user_driver_license_categories || [],
          form?.user_driver_license_categories || [],
          (category) => category.license_category.id,
          (category) => category.value || '',
          mapToDeletion,
          transformNewDriverLicenseCategories,
        ),
        name_on_card: normalizedNameOnCard,
        profile_picture: file ? file : undefined
      }

      const clientUserPayload = {
        client_user: payload,
      };

      return privateAxios.putForm(`/client/users/${contactId}`, clientUserPayload);
    },
    onSuccess,
    onError,
  });
}

export default useUpdateCompanyUser
